// 剧本配置预览页
// 预览生成的剧本配置，JSON 高亮展示

import React, { useEffect, useState, useCallback } from 'react'
import {
  Card,
  Button,
  Space,
  Tabs,
  Descriptions,
  Tag,
  message,
  Spin,
  Empty,
} from 'antd'
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import type { TabsProps } from 'antd'
import DOMPurify from 'dompurify'
import {
  getScriptGenerationConfigPreview,
  downloadScriptPackage,
} from '@/services/scriptGeneration'
import type { ScriptConfigPreview, ScriptNPC, ScriptEvent, ScriptDialogue } from '@/types/scriptGeneration'
import { handleApiError } from '@/utils/errorHandler'
import './ConfigPreview.css'

/**
 * JSON 语法高亮组件（XSS 防护）
 */
const JsonHighlight: React.FC<{ data: unknown }> = ({ data }) => {
  const jsonString = JSON.stringify(data, null, 2)

  const syntaxHighlight = (json: string): string => {
    // 先转义 HTML 特殊字符
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    // 添加语法高亮
    return json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = 'json-number'
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key'
          } else {
            cls = 'json-string'
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean'
        } else if (/null/.test(match)) {
          cls = 'json-null'
        }
        return `<span class="${cls}">${match}</span>`
      }
    )
  }

  // 使用 DOMPurify 清理 HTML，防止 XSS 攻击
  const safeHtml = DOMPurify.sanitize(syntaxHighlight(jsonString), {
    ALLOWED_TAGS: ['pre', 'span'],
    ALLOWED_ATTR: ['class'],
  })

  return (
    <pre
      className="json-highlight"
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  )
}

/**
 * 剧本配置预览页
 */
const ScriptGenerationConfigPreview: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()

  // 状态管理
  const [config, setConfig] = useState<ScriptConfigPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [copied, setCopied] = useState(false)

  /**
   * 处理无效 taskId - 导航回列表页
   */
  useEffect(() => {
    if (!taskId) {
      messageApi.error('无效的任务 ID')
      navigate('/script-generation', { replace: true })
    }
  }, [taskId, navigate, messageApi])

  /**
   * 加载配置预览
   */
  const loadConfig = useCallback(async () => {
    if (!taskId) return

    setLoading(true)
    try {
      const preview = await getScriptGenerationConfigPreview(taskId)
      setConfig(preview)
    } catch (error) {
      handleApiError(error, '加载配置预览失败', messageApi)
    } finally {
      setLoading(false)
    }
  }, [taskId, messageApi])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  /**
   * 返回详情页
   */
  const handleBack = useCallback(() => {
    if (taskId) {
      navigate(`/script-generation/${taskId}`)
    }
  }, [taskId, navigate])

  /**
   * 复制 JSON 配置
   */
  const handleCopy = useCallback(() => {
    if (!config) return

    const jsonString = JSON.stringify(config.config, null, 2)
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true)
      messageApi.success('已复制到剪贴板')
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {
      messageApi.error('复制失败')
    })
  }, [config, messageApi])

  /**
   * 导出剧本包
   */
  const handleExport = useCallback(async () => {
    if (!taskId) return

    setExporting(true)
    try {
      const blob = await downloadScriptPackage(taskId)
      // 创建下载链接
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `script-package-${taskId}.zip`
      link.click()
      URL.revokeObjectURL(link.href)
      messageApi.success('导出成功')
    } catch (error) {
      handleApiError(error, '导出失败', messageApi)
    } finally {
      setExporting(false)
    }
  }, [taskId, messageApi])

  /**
   * 渲染 NPC 列表
   */
  const renderNPCs = useCallback(() => {
    if (!config?.npcs || config.npcs.length === 0) {
      return <Empty description="暂无 NPC 数据" />
    }

    return (
      <div className="config-preview-grid">
        {config.npcs.map((npc: ScriptNPC) => (
          <Card key={npc.id} size="small" className="config-preview-item">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="NPC ID">{npc.id}</Descriptions.Item>
              <Descriptions.Item label="名称">{npc.name}</Descriptions.Item>
              <Descriptions.Item label="描述">{npc.description}</Descriptions.Item>
              <Descriptions.Item label="性格特征">
                <Space wrap>
                  {npc.personality.map((trait, index) => (
                    <Tag key={index} color="blue">
                      {trait}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
              {npc.initialDialogue && (
                <Descriptions.Item label="初始对话">
                  <div className="config-preview-dialogue">{npc.initialDialogue}</div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        ))}
      </div>
    )
  }, [config])

  /**
   * 渲染事件列表
   */
  const renderEvents = useCallback(() => {
    if (!config?.events || config.events.length === 0) {
      return <Empty description="暂无事件数据" />
    }

    return (
      <div className="config-preview-events">
        {config.events.map((event: ScriptEvent, index: number) => (
          <Card key={event.id} size="small" className="config-preview-event">
            <div className="event-header">
              <span className="event-index">#{index + 1}</span>
              <Tag color="geekblue">{event.type}</Tag>
            </div>
            <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
              <Descriptions.Item label="事件 ID">{event.id}</Descriptions.Item>
              <Descriptions.Item label="事件名称">{event.name}</Descriptions.Item>
              <Descriptions.Item label="触发条件">
                <code className="event-condition">{event.triggerCondition}</code>
              </Descriptions.Item>
              <Descriptions.Item label="事件描述">{event.description}</Descriptions.Item>
            </Descriptions>
          </Card>
        ))}
      </div>
    )
  }, [config])

  /**
   * 渲染对话列表
   */
  const renderDialogues = useCallback(() => {
    if (!config?.dialogues || config.dialogues.length === 0) {
      return <Empty description="暂无对话数据" />
    }

    return (
      <div className="config-preview-dialogues">
        {config.dialogues.map((dialogue: ScriptDialogue, index: number) => (
          <Card key={dialogue.id} size="small" className="config-preview-dialogue-item">
            <div className="dialogue-header">
              <span className="dialogue-index">#{index + 1}</span>
              <Tag color="cyan">NPC: {dialogue.npcId}</Tag>
            </div>
            <div className="dialogue-content">{dialogue.content}</div>
            {dialogue.context && (
              <div className="dialogue-context">上下文: {dialogue.context}</div>
            )}
          </Card>
        ))}
      </div>
    )
  }, [config])

  /**
   * Tab 配置
   */
  const tabItems: TabsProps['items'] = [
    {
      key: 'overview',
      label: '概览',
      children: config ? (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="剧本 ID">{config.script_id}</Descriptions.Item>
          <Descriptions.Item label="剧本名称">{config.scriptName}</Descriptions.Item>
          <Descriptions.Item label="版本">{config.version}</Descriptions.Item>
          <Descriptions.Item label="NPC 数量">{config.npcs.length}</Descriptions.Item>
          <Descriptions.Item label="事件数量">{config.events.length}</Descriptions.Item>
          <Descriptions.Item label="对话数量">{config.dialogues.length}</Descriptions.Item>
        </Descriptions>
      ) : <Spin />,
    },
    {
      key: 'npcs',
      label: `NPC (${config?.npcs.length || 0})`,
      children: renderNPCs(),
    },
    {
      key: 'events',
      label: `事件 (${config?.events.length || 0})`,
      children: renderEvents(),
    },
    {
      key: 'dialogues',
      label: `对话 (${config?.dialogues.length || 0})`,
      children: renderDialogues(),
    },
    {
      key: 'json',
      label: (
        <span>
          JSON 配置
          <Button
            type="link"
            size="small"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={handleCopy}
          >
            {copied ? '已复制' : '复制'}
          </Button>
        </span>
      ),
      children: config ? <JsonHighlight data={config.config} /> : <Spin />,
    },
  ]

  // 早期返回：处理无效 taskId
  if (!taskId) {
    return null
  }

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!config) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="配置不存在" />
      </div>
    )
  }

  return (
    <div className="script-generation-config-preview">
      {contextHolder}

      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回
            </Button>
            <span>剧本配置预览</span>
            <Tag color="blue">{config.version}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<CopyOutlined />} onClick={handleCopy}>
              复制配置
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              导出剧本包
            </Button>
          </Space>
        }
      >
        <Tabs items={tabItems} />
      </Card>
    </div>
  )
}

export default ScriptGenerationConfigPreview
